import { evaluateCondition } from '@folio/components/forms/form-builder/conditionalVisibilityUtils';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  type ConfigCustomFieldDto,
  configControllerGetPublicProductConfigQueryOptions,
} from 'korint-sdk';
import type { ConditionalVisibility } from 'korint-sdk/react';
import { sortBy } from 'lodash';
import type { InformationCardProps } from './InformationCard.type';
import { InformationArrayField } from './InformationArrayField';
import { defaultFormatValue, InformationField } from './InformationField';
import { getEditionMode, resolveFieldShortcutFromConfig } from './InformationFields';

type DynamicInformationFieldsProps = Pick<
  InformationCardProps,
  'productId' | 'fieldsConfig' | 'entity' | 'updateEntity'
> & {
  customFieldConfigs: ConfigCustomFieldDto[];
  redirectToForm?: InformationCardProps['redirectToForm'];
  allowEdition?: boolean;
  forceReadonly?: boolean;
};

const isShown = ({ display }: ConfigCustomFieldDto, customFields: Record<string, unknown>) =>
  ((display?.showWhen ?? []) as ConditionalVisibility[]).every(condition =>
    evaluateCondition(condition, customFields[condition.fieldName ?? '']),
  );

export function DynamicInformationFields({
  productId,
  fieldsConfig,
  entity,
  customFieldConfigs,
  updateEntity,
  redirectToForm,
  allowEdition,
  forceReadonly,
}: DynamicInformationFieldsProps) {
  const { data: steps } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
    select: config => sortBy(config.steps ?? [], 'position'),
  });

  const staticKeys = new Set(
    fieldsConfig.map(field => (typeof field === 'string' ? field : 'key' in field && field.key)),
  );
  const customFields = entity.customFields as Record<string, unknown>;

  const fields = steps.flatMap(step =>
    customFieldConfigs.filter(
      field =>
        field.display?.step === step.key &&
        !staticKeys.has(field.key) &&
        isShown(field, customFields),
    ),
  );

  return fields.map(field => {
    if (field.type === 'ARRAY') {
      return (
        <InformationArrayField
          key={field.key}
          fieldKey={field.key}
          productId={productId}
          fieldConfig={field}
          value={customFields[field.key]}
          editionMode={getEditionMode(field, { allowEdition, forceReadonly })}
          redirectToForm={redirectToForm}
          updateEntity={updateEntity}
        />
      );
    }

    const fieldParams = resolveFieldShortcutFromConfig(field.key, customFieldConfigs);
    if (!fieldParams) {
      return null;
    }

    const optionLabels = field.display?.optionLabels as Record<string, string> | undefined;

    return (
      <InformationField
        key={field.key}
        fieldKey={field.key}
        label={field.display?.label}
        productId={productId}
        redirectToForm={redirectToForm}
        updateEntity={updateEntity}
        editionMode={getEditionMode(field, { allowEdition, forceReadonly })}
        type={fieldParams.type}
        options={
          fieldParams.type === 'select'
            ? (field.validationRules?.find(({ option }) => option === 'enum')?.value as
                | string[]
                | undefined)
            : undefined
        }
        value={customFields[field.key] as string | number | boolean | undefined}
        formatValue={
          optionLabels
            ? value => optionLabels[String(value)] ?? defaultFormatValue[fieldParams.type](value)
            : undefined
        }
        validationRules={fieldParams.countries ? { countries: fieldParams.countries } : undefined}
      />
    );
  });
}
